// Versão: 1.00
// Data..: 2026-09-26
// Paginação client-side reutilizável para as listagens de Administração.
// - usePaged(items, pageSize): fatia a lista atual, corrige a página fora do
//   intervalo (ex.: após filtrar) e expõe os controles.
// - <Pager>: barra « Início · ‹ Anterior · Página X de Y · Próxima › · Fim »,
//   com o resumo "início–fim de total". Só deve ser renderizada quando total > pageSize.

import { useState } from 'react';

export interface Paged<T> {
  page: number;
  setPage: (p: number) => void;
  totalPages: number;
  currentPage: number;
  pageStart: number;
  pageItems: T[];
  total: number;
  pageSize: number;
}

// Estado de paginação derivado da lista já filtrada/ordenada.
export function usePaged<T>(items: T[], pageSize = 20): Paged<T> {
  const [page, setPage] = useState(1);
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pageItems = items.slice(pageStart, pageStart + pageSize);
  return { page, setPage, totalPages, currentPage, pageStart, pageItems, total, pageSize };
}

// Barra de navegação de páginas. `noun` é o rótulo do resumo (ex.: "usuários").
export function Pager<T>({ paged, noun }: { paged: Paged<T>; noun?: string }) {
  const { currentPage, totalPages, pageStart, pageSize, total, setPage } = paged;
  if (total <= pageSize) return null;
  return (
    <div className="flex items-center justify-between flex-wrap gap-2 mt-3">
      <span className="text-dim text-sm">
        {pageStart + 1}–{Math.min(pageStart + pageSize, total)} de {total}{noun ? ` ${noun}` : ''}
      </span>
      <div className="flex items-center gap-2">
        <button className="btn btn-sm btn-ghost" disabled={currentPage <= 1} onClick={() => setPage(1)} title="Primeira página">« Início</button>
        <button className="btn btn-sm btn-ghost" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>‹ Anterior</button>
        <span className="text-sm">Página {currentPage} de {totalPages}</span>
        <button className="btn btn-sm btn-ghost" disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)}>Próxima ›</button>
        <button className="btn btn-sm btn-ghost" disabled={currentPage >= totalPages} onClick={() => setPage(totalPages)} title="Última página">Fim »</button>
      </div>
    </div>
  );
}
